import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link to="/" className="legal-logo">🎙️ EchoMemoir</Link>
      </header>

      <main className="legal-main">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: February 6, 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            EchoMemoir ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains 
            how we collect, use, disclose, and safeguard your information when you use our family storytelling platform. 
            Please read this policy carefully. By using EchoMemoir, you consent to the practices described herein.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>

          <h3>2.1 Account Information</h3>
          <p>
            When you create an account, we collect your name, email address, and encrypted password. We may also 
            collect profile information such as display name and avatar.
          </p>

          <h3>2.2 Audio Recordings and Stories</h3>
          <p>
            We collect audio recordings you upload, along with the transcriptions and AI-enhanced versions generated 
            from them. This is the core content of the Service and is stored to provide functionality to you and your 
            family circle members.
          </p>

          <h3>2.3 Voice Data</h3>
          <p>
            If you use our voice cloning feature, we collect voice samples that are processed to create synthetic voice 
            profiles. Voice samples are transmitted to third-party voice synthesis providers for processing. Voice 
            profiles are associated with your family circle and are only accessible to authorized members.
          </p>

          <h3>2.4 Photos and Media</h3>
          <p>
            We store photos and other media you attach to your stories. These files are stored securely in cloud 
            storage (Amazon Web Services S3) and are accessible only to members of the relevant family circle.
          </p>

          <h3>2.5 Usage Data</h3>
          <p>
            We automatically collect certain information about your device and usage patterns, including IP address, 
            browser type, operating system, pages visited, and timestamps. This data helps us improve the Service.
          </p>

          <h3>2.6 Cookies</h3>
          <p>
            We use authentication tokens stored in your browser's local storage to maintain your login session. We do 
            not use tracking cookies or third-party advertising cookies.
          </p>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve the EchoMemoir service</li>
            <li>Process and transcribe audio recordings using AI services</li>
            <li>Generate AI-enhanced story versions from your transcriptions</li>
            <li>Create and manage voice profiles for voice cloning features</li>
            <li>Send you email notifications about activity in your family circles</li>
            <li>Send account-related emails (confirmation, password reset, security alerts)</li>
            <li>Respond to your support requests and communications</li>
            <li>Detect, prevent, and address security issues and abuse</li>
          </ul>
        </section>

        <section>
          <h2>4. Third-Party Services</h2>
          <p>We use the following third-party services to operate EchoMemoir:</p>
          <ul>
            <li><strong>Amazon Web Services (AWS):</strong> Cloud hosting, file storage (S3), and infrastructure</li>
            <li><strong>MongoDB Atlas:</strong> Database hosting for account and story data</li>
            <li><strong>OpenAI:</strong> AI transcription and story enhancement processing</li>
            <li><strong>Voice cloning providers:</strong> Voice synthesis and cloning services</li>
            <li><strong>Google reCAPTCHA:</strong> Bot prevention on login and registration forms</li>
            <li><strong>Gmail/SMTP:</strong> Email delivery for notifications and account communications</li>
          </ul>
          <p>
            These providers have their own privacy policies governing their use of your data. We only share the 
            minimum data necessary for each service to function.
          </p>
        </section>

        <section>
          <h2>5. Data Sharing and Disclosure</h2>
          <p>We do not sell, rent, or trade your personal information. We may share your information only in the following circumstances:</p>
          <ul>
            <li><strong>Within Family Circles:</strong> Stories, comments, and profile information are shared with members of your family circles as part of the Service's core functionality</li>
            <li><strong>Service Providers:</strong> With third-party providers as described in Section 4, solely to operate the Service</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
            <li><strong>Safety:</strong> When we believe disclosure is necessary to protect the rights, safety, or property of our users or the public</li>
          </ul>
        </section>

        <section>
          <h2>6. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your data, including encrypted connections 
            (HTTPS/TLS), password hashing using bcrypt, secure cloud storage with access controls, and authentication 
            tokens for session management. However, no method of electronic storage is 100% secure, and we cannot 
            guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>7. Data Retention</h2>
          <p>
            We retain your data for as long as your account is active. Audio recordings, stories, and associated 
            content are stored indefinitely unless you delete them or delete your account. When you delete your 
            account, we remove your personal information and account data. Stories shared within family circles 
            may be retained in anonymized form for other circle members.
          </p>
        </section>

        <section>
          <h2>8. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
            <li><strong>Correction:</strong> Update or correct inaccurate personal information</li>
            <li><strong>Deletion:</strong> Delete your account and associated data through the Settings page</li>
            <li><strong>Portability:</strong> Export your stories as PDF documents</li>
            <li><strong>Notification Control:</strong> Manage email notification preferences in Settings</li>
            <li><strong>Consent Withdrawal:</strong> Withdraw consent for voice cloning at any time</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:support@echomemoir.ai">support@echomemoir.ai</a>.
          </p>
        </section>

        <section>
          <h2>9. California Privacy Rights (CCPA)</h2>
          <p>
            If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), 
            including the right to know what personal information is collected, the right to request deletion, and the 
            right to opt out of the sale of personal information. We do not sell personal information. To submit a 
            request, contact us at <a href="mailto:support@echomemoir.ai">support@echomemoir.ai</a>.
          </p>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            EchoMemoir is not intended for children under 13 years of age. We do not knowingly collect personal 
            information from children under 13. If we discover that we have collected data from a child under 13, 
            we will take steps to delete it promptly. Family stories may include content about children, but account 
            creation requires the user to be at least 13 years old (or have parental consent if between 13 and 18).
          </p>
        </section>

        <section>
          <h2>11. International Users</h2>
          <p>
            EchoMemoir is operated from the United States. If you are accessing the Service from outside the United 
            States, your information may be transferred to, stored, and processed in the United States. By using the 
            Service, you consent to such transfer and processing.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes by posting 
            the updated policy on the Service and, where appropriate, by email. Your continued use of the Service 
            after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>13. Contact Us</h2>
          <p>
            If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p>
            <strong>EchoMemoir</strong><br />
            Email: <a href="mailto:support@echomemoir.ai">support@echomemoir.ai</a><br />
            Website: <a href="https://echomemoir.ai">echomemoir.ai</a>
          </p>
        </section>
      </main>

      <footer className="legal-footer">
        <Link to="/privacy">Privacy Policy</Link>
        <span>·</span>
        <Link to="/terms">Terms of Service</Link>
        <span>·</span>
        <span>© {new Date().getFullYear()} EchoMemoir</span>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;